import type {
  RttsAcquireErrorCode,
  RttsAcquireRequest,
  RttsAcquireResult,
  RttsGateFields,
} from '../types';
import { clearMockJobs, seedMockJob } from './rttsMockJobs';

export type RttsMockConfig = {
  /** When false, fixtures render as not entitled (UPGRADE / MEMBER_LOCKED). */
  entitled: boolean;
  /**
   * Force acquire to fail with a typed reason, a network error, or succeed.
   * Use for error-path UI testing.
   */
  acquireError: RttsAcquireErrorCode | 'network' | null;
  /** Extras for typed failures (e.g. vod_too_long minutes). */
  acquireErrorExtras: {
    max_minutes?: number;
    vod_minutes?: number;
    status?: number;
    message?: string;
  } | null;
  /** Force every job poll to return failed. */
  forceJobFailed: boolean;
  /** Fail when the job reaches this status (before advancing). */
  jobFailAtStatus: string | null;
  jobFailErrorCode: string | null;
};

const DEFAULT_CONFIG: RttsMockConfig = {
  entitled: true,
  acquireError: null,
  acquireErrorExtras: null,
  forceJobFailed: false,
  jobFailAtStatus: null,
  jobFailErrorCode: null,
};

let mockConfig: RttsMockConfig = { ...DEFAULT_CONFIG };

/** Test/dev helper to exercise entitlement and acquire error paths. */
export function configureRttsMock(partial: Partial<RttsMockConfig>): void {
  mockConfig = { ...mockConfig, ...partial };
}

export function resetRttsMock(): void {
  mockConfig = { ...DEFAULT_CONFIG };
  clearMockJobs();
  seedDeliveredFixtureJobs();
}

export function getRttsMockConfig(): RttsMockConfig {
  return { ...mockConfig };
}

const BASE_FIXTURES: Omit<RttsGateFields, 'rtts_entitled'>[] = [
  {
    archive_id: 'league-demo:archive:twitch:generate',
    matchup: 'Team A @ Team B',
    played_at: '2026-08-08T02:14:00Z',
    can_generate: true,
    existing_reel: null,
    week_label: 'Week 8',
    season_label: 'Season 4',
    broadcaster_handle: 'alpha_cast',
    thumbnail_url: 'https://example.com/thumbs/generate.jpg',
  },
  {
    archive_id: 'league-demo:archive:twitch:regenerate',
    matchup: 'Team C @ Team D',
    played_at: '2026-08-07T01:00:00Z',
    can_generate: true,
    existing_reel: {
      job_id: 'hl-delivered-1',
      status: 'delivered',
      signed_url: 'https://example.com/mock-reel-1.mp4',
      expires_at: '2026-08-16T00:00:00Z',
      reel_duration_sec: 38,
    },
    week_label: 'Week 7',
    season_label: 'Season 4',
    broadcaster_handle: 'charlie_cast',
    thumbnail_url: 'https://example.com/thumbs/regenerate.jpg',
  },
  {
    archive_id: 'league-demo:archive:twitch:watch',
    matchup: 'Team E @ Team F',
    played_at: '2026-08-06T20:30:00Z',
    can_generate: false,
    existing_reel: {
      job_id: 'hl-delivered-2',
      status: 'delivered',
      signed_url: 'https://example.com/mock-reel-2.mp4',
      expires_at: '2026-08-16T00:00:00Z',
      reel_duration_sec: 45,
    },
    week_label: 'Week 6',
    season_label: 'Season 4',
    broadcaster_handle: 'echo_cast',
    thumbnail_url: 'https://example.com/thumbs/watch.jpg',
  },
  {
    archive_id: 'league-demo:archive:twitch:passive',
    matchup: 'Team G @ Team H',
    played_at: '2026-08-05T18:00:00Z',
    can_generate: false,
    existing_reel: null,
    week_label: 'Week 5',
    season_label: 'Season 4',
    broadcaster_handle: 'golf_cast',
    thumbnail_url: null,
  },
];

function withEntitlement(fixture: Omit<RttsGateFields, 'rtts_entitled'>): RttsGateFields {
  return {
    ...fixture,
    rtts_entitled: mockConfig.entitled,
    existing_reel: fixture.existing_reel
      ? { ...fixture.existing_reel }
      : null,
  };
}

export async function mockListRttsGames(_leagueId: string): Promise<RttsGateFields[]> {
  return BASE_FIXTURES.map(withEntitlement);
}

/**
 * Map an arbitrary archive id onto a fixture so the per-game mount can exercise
 * every button state without requiring backend gate fields yet.
 */
export async function mockGetRttsGateFields(
  _leagueId: string,
  archiveId: string
): Promise<RttsGateFields> {
  const exact = BASE_FIXTURES.find((f) => f.archive_id === archiveId);
  if (exact) return withEntitlement(exact);

  let hash = 0;
  for (let i = 0; i < archiveId.length; i += 1) {
    hash = (hash + archiveId.charCodeAt(i)) % BASE_FIXTURES.length;
  }
  const template = BASE_FIXTURES[hash]!;
  return withEntitlement({
    ...template,
    archive_id: archiveId,
  });
}

export async function mockAcquireHighlight(
  _leagueId: string,
  body: RttsAcquireRequest
): Promise<RttsAcquireResult> {
  if (mockConfig.acquireError === 'network') {
    throw new Error('Network error (mock)');
  }
  if (mockConfig.acquireError) {
    const extras = mockConfig.acquireErrorExtras || {};
    return {
      success: false,
      error: mockConfig.acquireError,
      ...extras,
    };
  }

  // // BACKEND-DEP: backend should reject regenerate/force acquire when existing_reel is present
  const fixture = BASE_FIXTURES.find((f) => f.archive_id === body.archive_id);
  if (fixture?.existing_reel) {
    return { success: false, error: 'reel_already_exists' };
  }

  const jobId = `hl-mock-${body.archive_id.slice(-8)}-${Date.now()}`;
  seedMockJob(jobId, { status: 'downloading' });

  return {
    success: true,
    job: {
      id: jobId,
      status: 'downloading',
    },
  };
}

/** Seed delivered jobs for existing_reel fixtures so consume/re-fetch works. */
export function seedDeliveredFixtureJobs(): void {
  for (const fixture of BASE_FIXTURES) {
    const reel = fixture.existing_reel;
    if (!reel || reel.status !== 'delivered') continue;
    seedMockJob(reel.job_id, {
      status: 'delivered',
      signed_url: reel.signed_url ?? null,
      expires_at: reel.expires_at ?? null,
      reel_duration_sec: reel.reel_duration_sec ?? 42,
    });
  }
}

seedDeliveredFixtureJobs();
